import React, { useState, useEffect } from "react";
import { api } from "../../utils/api";
import { useParams } from "react-router-dom";
import { Button, Link } from "@mui/material";
import ModalPopup from "../../components/ModalPopup";
import SubscriptionForm from "./Forms/SubscriptionForm";
import { useNavigate } from "react-router-dom";
import GlobalLoadingSpinner from "../../components/GlobalLoadingSpinner";
import { DATA_GRID_STYLES } from "../../components/BasicUserInfo";
import { COLORS } from "../../utils/colors";
import IconButton from "@mui/material/IconButton";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ModalImage from "react-modal-image";
import RedemptionHistory from "../Customer/RedemptionHistory";
import AdminContractManager from "../../components/AdminContractManager";
import AdminResyncFromStripe from "../../components/AdminResyncFromStripe";
import SubscriptionStripeCard from "./SubscriptionStripeCard";
import ContractAcceptanceModal from "../../components/ContractAcceptanceModal";
import { subscriptionRequiresContract } from "../../utils/subscriptionContractGate";
import { isHiddenFromCustomerProduct } from "../../utils/hiddenFromCustomerProduct";

const SubscriptionSingle = () => {
  let navigate = useNavigate();
  const params = useParams();

  const [loading, setLoading] = useState(true);
  const [editSubscriptionState, setEditSubscriptionState] = useState(false);

  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [deleteConfirmationIsOpen, setDeleteConfirmationIsOpen] = useState(false);
  const [redemptionHistory, setRedemptionHistory] = useState([]);
  const [redemptionImages, setRedemptionImages] = useState({});
  const [contractSigned, setContractSigned] = useState(false);
  const [checkingContract, setCheckingContract] = useState(true);
  const [contractModalOpen, setContractModalOpen] = useState(false);
  const [contractModalData, setContractModalData] = useState(null);

  const isCustomerPortal = sessionStorage.getItem("selectedPortal") === "Customer";

  useEffect(() => {
    if (
      isCustomerPortal &&
      selectedSubscription &&
      isHiddenFromCustomerProduct(selectedSubscription.product)
    ) {
      navigate("/my-subscriptions", { replace: true });
    }
  }, [isCustomerPortal, navigate, selectedSubscription]);

  const subscriptionForDisplay =
    selectedSubscription &&
    subscriptionRequiresContract(
      selectedSubscription.product,
      selectedSubscription
    ) &&
    !contractSigned
      ? { ...selectedSubscription, qrcode: null }
      : selectedSubscription;

  const fetchSubscriptionDetails = React.useCallback(async () => {
    try {
      const response = await api("v1/subscription/" + params.idSubscription, "GET", null);
      if (response.data.success) {
        const imagePromises = (response.data.data.subscription.redemptions || []).map((redemption) =>
          api(`v1/fetchFileFromCloud/url/${encodeURIComponent(redemption.image)}`, "GET")
        );
        const imageResponses = await Promise.all(imagePromises);
        const images = {};
        (response.data.data.subscription.redemptions || []).forEach((redemption, index) => {
          images[redemption._id] = imageResponses[index]?.data?.data?.url;
        });
        setRedemptionImages(images);
        const sub = response.data.data.subscription;
        setSelectedSubscription(sub);
      }
    } catch (error) {
      console.error("API error:", error);
    } finally {
      setLoading(false);
    }
  }, [params.idSubscription]);

  useEffect(() => {
    fetchSubscriptionDetails();
  }, [fetchSubscriptionDetails]);

  const checkContractStatus = React.useCallback(async () => {
    if (!selectedSubscription?._id) return;

    setCheckingContract(true);
    try {
      const response = await api(`v1/contract/subscription/${selectedSubscription._id}`, "GET");

      if (response.data.success) {
        const contractData = response.data.data;

        if (!contractData.requiresContract) {
          setContractSigned(true);
          setContractModalOpen(false);
          setContractModalData(null);
          return;
        }

        const isSigned = !!contractData.isSigned;
        setContractSigned(isSigned);

        if (!isSigned && isCustomerPortal) {
          const contract = contractData.contract;
          if (contract && (contract.status === "pending" || contract.contractStatus === "pending")) {
            setContractModalData(contract);
            setContractModalOpen(true);
          } else {
            const pendingResponse = await api(
              `v1/contract/check-pending/${selectedSubscription._id}`,
              "GET"
            );
            if (pendingResponse.data.success && pendingResponse.data.data) {
              setContractModalData(pendingResponse.data.data);
              setContractModalOpen(true);
            }
          }
        } else {
          setContractModalOpen(false);
          setContractModalData(null);
        }
      } else if (isCustomerPortal) {
        setContractSigned(false);
        setSelectedSubscription((prev) => (prev ? { ...prev, qrcode: null } : prev));
      } else {
        setContractSigned(true);
      }
    } catch (error) {
      console.error("Error checking contract status:", error);
      if (isCustomerPortal) {
        setContractSigned(false);
        setSelectedSubscription((prev) => (prev ? { ...prev, qrcode: null } : prev));
      } else {
        setContractSigned(true);
      }
    } finally {
      setCheckingContract(false);
    }
  }, [selectedSubscription?._id, isCustomerPortal]);

  useEffect(() => {
    if (selectedSubscription) {
      checkContractStatus();
    }
  }, [selectedSubscription, checkContractStatus]);

  useEffect(() => {
    if (!isCustomerPortal) return;
    const onVisible = () => {
      if (document.visibilityState === "visible" && selectedSubscription?._id) {
        checkContractStatus();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [isCustomerPortal, selectedSubscription?._id, checkContractStatus]);

  const handleContractAccepted = async () => {
    setContractSigned(true);
    setContractModalOpen(false);
    setContractModalData(null);
    await fetchSubscriptionDetails();
  };

  const handleContractModalClose = () => {
    if (contractSigned) {
      setContractModalOpen(false);
      setContractModalData(null);
    } else if (isCustomerPortal) {
      setContractModalOpen(false);
      setTimeout(() => checkContractStatus(), 300);
    }
  };

  const handleDeleteSubscriptionConfirm = async () => {
    setLoading(true);
    try {
      const response = await api("v1/subscription/" + selectedSubscription?._id, "DELETE", null);
      if (response.data.success) {
        window.location.replace("/subscription");
      }
    } catch (error) {
      console.error("API error:", error);
    }
    toggleDisplayDeleteConfirm();
    setLoading(false); //once api call is finished.
  };

  const toggleDisplayDeleteConfirm = () => {
    setDeleteConfirmationIsOpen(!deleteConfirmationIsOpen);
  };

  const editSubscription = () => {
    setEditSubscriptionState((prevValue) => !prevValue);
    // setOpenModal(true);
  };

  const reloadSubscriptionDetail = (updateSubscriptionDetail) => {
    setEditSubscriptionState((prevValue) => !prevValue);
    setSelectedSubscription(updateSubscriptionDetail);
  };

  function formatDate(dateString) {
    const options = { year: "numeric", month: "long", day: "numeric" };
    const formattedDate = new Date(dateString).toLocaleDateString(undefined, options);

    return formattedDate;
  }

  return (
    selectedSubscription && (
      <div className="page partner-user-single w-full flex flex-column items-center min-h-h-screen p-8 gap-8">
        {loading && <GlobalLoadingSpinner />}

        {/* Page Title */}
        <div className="pageTitle w-full flex justify-between">
          <h2>Subscription Details</h2>
          <div>
            <IconButton onClick={() => navigate(-1)} sx={{ border: "1px solid grey" }}>
              <ArrowBackIcon />
            </IconButton>
          </div>
        </div>

        {/* Subscription Single Content */}
        <div className="w-full flex flex-wrap gap-4 justify-center">
          {(() => {
            if (!subscriptionForDisplay) return null;

            const now = new Date();
            const isExpired =
              subscriptionForDisplay.validUntilDateUTC &&
              new Date(subscriptionForDisplay.validUntilDateUTC) < now;
            const isFuture =
              subscriptionForDisplay.validFromDateUTC &&
              new Date(subscriptionForDisplay.validFromDateUTC) > now;
            const isValid = !isExpired && !isFuture;

            const requiresContract = subscriptionRequiresContract(
              subscriptionForDisplay.product,
              subscriptionForDisplay
            );

            const canShowQRCode = !requiresContract || contractSigned;
            const qrCode = subscriptionForDisplay?.qrcode;

            if (checkingContract) {
              return (
                <div className="subscriptionQrCode flex flex-column w-full justify-center items-center p-4 bg-gray-100 rounded">
                  <p className="text-gray-600">Checking contract status...</p>
                </div>
              );
            }

            if (isValid && qrCode && canShowQRCode) {
              return (
                <div className="subscriptionQrCode flex flex-column w-full justify-center items-center">
                  <img src={qrCode} alt="Subscription QR code" />
                </div>
              );
            } else if (isValid && !canShowQRCode && requiresContract) {
              return (
                <div className="subscriptionQrCode flex flex-column w-full justify-center items-center p-4 bg-gray-100 rounded">
                  <p className="text-gray-600">
                    QR code will be available after signing the subscription contract
                  </p>
                  <p className="text-gray-500 text-sm mt-2">
                    Please sign the contract to access your QR code
                  </p>
                </div>
              );
            } else if (qrCode) {
              return (
                <div className="subscriptionQrCode flex flex-column w-full justify-center items-center p-4 bg-gray-100 rounded">
                  <p className="text-gray-600">
                    {isExpired
                      ? `QR code expired on ${formatDate(subscriptionForDisplay.validUntilDateUTC)}`
                      : isFuture
                      ? `QR code will be available from ${formatDate(subscriptionForDisplay.validFromDateUTC)}`
                      : "QR code not available"}
                  </p>
                </div>
              );
            }
            return null;
          })()}
          <div className="flex self-center">
            <h4 className="font-bold"></h4>
            <div className={DATA_GRID_STYLES}>
              <span>Start Date</span>
              <span>{selectedSubscription.startDateUTC ? formatDate(selectedSubscription.startDateUTC) : "n/a"}</span>
              <span>Valid Until: </span>
              <span>
                {selectedSubscription.validUntilDateUTC ? formatDate(selectedSubscription.validUntilDateUTC) : "n/a"}
              </span>
              <span>Next Billing Date: </span>
              <span>
                {selectedSubscription.nextBillingDateUTC ? formatDate(selectedSubscription.nextBillingDateUTC) : "n/a"}
              </span>
              <span>Created On</span>
              <span>{selectedSubscription.createdOnUTC ? formatDate(selectedSubscription.createdOnUTC) : "n/a"} </span>
              <span>Active: </span>
              <span>{selectedSubscription.active ? "Yes" : "No"} </span>
            </div>
          </div>

          {sessionStorage.getItem("selectedPortal") == "Administrator" && (
            <div className="actionItems w-full flex flex-wrap gap-4 justify-center items-center">
              <Button
                sx={{ backgroundColor: COLORS.priorityBlue.hex }}
                className="self-start m-4"
                variant="contained"
                onClick={editSubscription}
              >
                Edit Subscription
              </Button>
              {selectedSubscription.stripeId && (
                <div className="m-4">
                  <AdminResyncFromStripe
                    subscriptionId={selectedSubscription._id}
                    hasStripeId={!!selectedSubscription.stripeId}
                    onSuccess={() => fetchSubscriptionDetails()}
                  />
                </div>
              )}
              <Button
                className="self-start bg-red-600 m-4"
                variant="contained"
                color="error"
                onClick={() => {
                  toggleDisplayDeleteConfirm(true);
                }}
              >
                Delete Subscription
              </Button>
            </div>
          )}

          {sessionStorage.getItem("selectedPortal") === "Administrator" && (
            <SubscriptionStripeCard
              subscriptionId={selectedSubscription._id}
              hasStripeId={!!selectedSubscription.stripeId}
              onResync={() => fetchSubscriptionDetails()}
            />
          )}

          <div className="flex w-full gap-4 flex-wrap justify-start">
            {selectedSubscription.vehicle && (
              <div className="w-[350px] sm:w-[480px] cursor-pointer">
                <h4 className="font-bold">Vehicle</h4>
                <div
                  onClick={() => navigate(`/vehicle/${selectedSubscription.vehicle._id}`)}
                  className={`${DATA_GRID_STYLES} cursor-pointer`}
                >
                  <span>Rego</span>
                  <span>{selectedSubscription.vehicle.rego}</span>
                  <span>VIN</span>
                  <span>{selectedSubscription.vehicle.vin}</span>
                  <span>Make</span>
                  <span>{selectedSubscription.vehicle.make}</span>
                  <span>Model</span>
                  <span>{selectedSubscription.vehicle.rego}</span>
                  <span>Model Year</span>
                  <span>{selectedSubscription.vehicle.modelYear}</span>
                  <span>Color</span>
                  <span>{selectedSubscription.vehicle.color}</span>
                  <span>AGE</span>
                  <span>{selectedSubscription.vehicle.age}</span>
                  <span>ACTIVATED</span>
                  <span>{selectedSubscription.vehicle.active ? "Yes" : "No"}</span>
                  <span>CREATED ON</span>
                  <span>
                    {selectedSubscription.vehicle.createdOnUTC
                      ? formatDate(selectedSubscription.vehicle.createdOnUTC)
                      : "n/a"}
                  </span>
                </div>
              </div>
            )}

            {selectedSubscription.product && (
              <div className="w-[350px] sm:w-[480px] cursor-pointer">
                <h4 className="font-bold">Product</h4>
                <div className={DATA_GRID_STYLES}>
                  <span>Name</span>
                  <span>{selectedSubscription.product.name}</span>
                  <span>Type</span>
                  <span>{selectedSubscription.product.type}</span>
                  <span>Subscription Duration</span>
                  <span>
                    {selectedSubscription.product.subscriptionDuration}{" "}
                    {parseInt(selectedSubscription.product.subscriptionDuration) == 1 ? "month" : "months"}
                  </span>
                  <span>
                    {(() => {
                      const interval =
                        selectedSubscription.billingInterval ||
                        selectedSubscription.product.interval;
                      if (interval === "month") return "Monthly price";
                      if (interval === "year") return "Yearly price";
                      if (interval === "week") return "Weekly price";
                      if (interval === "day") return "Daily price";
                      return "Price";
                    })()}
                  </span>
                  <span>
                    {selectedSubscription.purchasedAmount ??
                      selectedSubscription.purchasedMonthlyPrice ??
                      selectedSubscription.product.price}
                  </span>
                  <span>RRP</span>
                  <span>{selectedSubscription.product.rrp}</span>
                </div>
              </div>
            )}

            {selectedSubscription.vehicle && (
              <div className="cursor-pointer">
                <RedemptionHistory vehicles={selectedSubscription.vehicle} />
              </div>
            )}

            {/* Admin Contract Management - Only show for admin users */}
            {sessionStorage.getItem("selectedPortal") === "Administrator" && (
              <div className="w-[350px] sm:w-[480px]">
                <h3>Contract Management</h3>
                <AdminContractManager
                  subscriptionId={selectedSubscription._id}
                  onContractUpdated={() => {
                    // Optionally refresh subscription data
                    console.log("Contract updated, subscription data may need refresh");
                  }}
                />
              </div>
            )}

          </div>
        </div>

        {/* // Edit Modal HTML */}

        <ModalPopup
          showModal={editSubscriptionState}
          handleShowModal={editSubscription}
          popupTitle={"Edit Subscription"}
          popupContent={
            <SubscriptionForm
              selectedSubscription={selectedSubscription}
              callback={reloadSubscriptionDetail}
              cancelModal={editSubscription}
            />
          }
          cancelButtonText=""
          confirmButtonText=""
          confirmCallbackAction=""
        />

        {/* Delete Subscription Confimation Modal */}
        <ModalPopup
          showModal={deleteConfirmationIsOpen}
          handleShowModal={toggleDisplayDeleteConfirm}
          popupTitle={"Delete Subscription"}
          popupContent={<div>Are you sure you want to delete this subscription?</div>}
          cancelButtonText={"Cancel"}
          confirmButtonText={"Delete"}
          confirmCallbackAction={handleDeleteSubscriptionConfirm}
        />

        {isCustomerPortal && contractModalData && (
          <ContractAcceptanceModal
            isOpen={contractModalOpen}
            onClose={handleContractModalClose}
            contractData={contractModalData}
            onContractAccepted={handleContractAccepted}
          />
        )}
      </div>
    )
  );
};

export default SubscriptionSingle;
